import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactElement } from 'react';
import {
  ComboBox,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Text,
  type Key,
} from 'react-aria-components';
import { peopleLookupQuery } from '../api/awards';
import { useDebounced } from '../awards/useDebounced';
import { Icon, cx } from '../ui';
import f from '../ui/fields.module.css';
import s from './Settings.module.css';

export interface PickedPerson {
  id: string;
  name: string;
  jobTitle: string | null;
  sector: { id: string; name: string } | null;
}

/**
 * Colleague search (names, titles and sectors only, APP-030) for adding a delegate or choosing a
 * letter signer. Picking someone hands them over and clears the field.
 */
export function PersonPicker({
  label,
  description,
  exclude,
  onPick,
  isDisabled = false,
}: {
  label: string;
  description: string;
  /** People who can't be picked here (already delegates, the Sector Lead). */
  exclude: ReadonlySet<string>;
  onPick: (person: PickedPerson) => void;
  isDisabled?: boolean;
}): ReactElement {
  const [text, setText] = useState('');
  const term = useDebounced(text.trim(), 250);
  const lookup = useQuery(peopleLookupQuery(term));
  const searching = text.trim().length >= 2;
  const found = lookup.data?.items;
  const items = useMemo(
    () => (searching ? (found ?? []).filter((p) => !exclude.has(p.id)) : []),
    [searching, found, exclude],
  );
  const empty = (): string => {
    if (term.length < 2) return 'Type at least two letters of a name.';
    if (lookup.isFetching) return 'Searching…';
    if (lookup.isError) return 'We couldn’t search just now. Try again in a moment.';
    if ((found?.length ?? 0) > 0) return 'Everyone who matches is already here.';
    return `No one matches “${term}”. Check the spelling, or try a last name.`;
  };
  const pick = (key: Key | null): void => {
    if (key === null) return;
    const person = items.find((p) => p.id === key);
    if (!person) return;
    onPick(person);
    setText('');
  };
  return (
    <ComboBox
      className={cx(f.field, s.picker)}
      inputValue={text}
      onInputChange={setText}
      value={null}
      onChange={pick}
      items={items}
      menuTrigger="input"
      allowsEmptyCollection={searching}
      isDisabled={isDisabled}
    >
      <Label className={s.pickerLabel}>{label}</Label>
      <div className={f.body}>
        <div className={s.searchBox}>
          <Icon name="search" size={18} className={s.searchIcon} />
          <Input className={s.searchInput} placeholder="Search people by name" />
        </div>
        <Text slot="description" className={s.pickerHint}>
          {description}
        </Text>
      </div>
      <Popover className={s.pickerPopover} placement="bottom start">
        <ListBox
          className={s.pickerList}
          renderEmptyState={() => <p className={s.pickerEmpty}>{empty()}</p>}
        >
          {(person: (typeof items)[number]) => (
            <ListBoxItem id={person.id} textValue={person.name} className={s.pickerOption}>
              <span className={s.pickerName}>{person.name}</span>
              <span className={s.pickerMeta}>
                {[person.jobTitle, person.sector?.name].filter(Boolean).join(' · ') || 'Areté'}
              </span>
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </ComboBox>
  );
}
